// Privacy Policy + Terms — required for App Store submission and for any user
import PageTitle from "../components/PageTitle";
// signing in with email. Plain English, single-author-app honesty. NOT legal
// advice — review with a lawyer before you launch to paid users.

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

type Tab = "privacy" | "terms";

export default function Legal() {
  const navigate = useNavigate();
  const params = useParams<{ doc?: string }>();
  const initial: Tab = params.doc === "terms" ? "terms" : "privacy";
  const [tab, setTab] = useState<Tab>(initial);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-sm text-slate-400">
          ← Back
        </button>
        <PageTitle>Legal</PageTitle>
      </div>

      <div className="flex bg-slate-900 border border-slate-800 rounded-lg p-1">
        <button
          onClick={() => setTab("privacy")}
          className={`flex-1 py-2 rounded text-sm font-medium ${
            tab === "privacy" ? "bg-slate-800 text-emerald-400" : "text-slate-400"
          }`}
        >
          Privacy
        </button>
        <button
          onClick={() => setTab("terms")}
          className={`flex-1 py-2 rounded text-sm font-medium ${
            tab === "terms" ? "bg-slate-800 text-emerald-400" : "text-slate-400"
          }`}
        >
          Terms
        </button>
      </div>

      <article className="prose prose-invert prose-sm max-w-none p-4 rounded-2xl bg-slate-900 border border-slate-800 text-slate-200 space-y-4">
        {tab === "privacy" ? <PrivacyContent /> : <TermsContent />}
      </article>

      <div className="text-xs text-slate-500 text-center">
        Questions? Email <a href="mailto:hello@thefoundationbriefing.com" className="text-emerald-400 underline">hello@thefoundationbriefing.com</a>.
      </div>
    </div>
  );
}

function PrivacyContent() {
  return (
    <>
      <h2 className="text-lg font-semibold text-slate-100">Privacy Policy</h2>
      <p className="text-xs text-slate-400">Last updated: May 2026</p>

      <Section title="The short version">
        <p>
          Tallyplate stores the meals you log, your photos, your weight, and your goals so the app
          can show them back to you across your devices. We don't sell your data, we don't run ads, and
          we don't share your information with third parties for marketing.
        </p>
      </Section>

      <Section title="What we collect">
        <ul>
          <li><strong>Account info</strong>: your email address (for sign-in) and password (hashed by Supabase Auth).</li>
          <li><strong>Meal logs</strong>: photos, captions, food items, macros, dates, and which meal section (breakfast/lunch/dinner/snack).</li>
          <li><strong>Health info you enter</strong>: weight history, height, age, biological sex, activity level, calorie/macro goals, water intake.</li>
          <li><strong>Usage counters</strong>: number of AI estimates per day (to enforce rate limits — not analytics).</li>
        </ul>
        <p className="text-xs text-slate-400">
          We do not collect: location, contacts, microphone, browsing history, device identifiers, or anything else not listed above.
        </p>
      </Section>

      <Section title="Where it's stored">
        <ul>
          <li><strong>Supabase</strong> (PostgreSQL + object storage, hosted in the US) — your meal rows, weights, goals, prefs, and photos. Row-Level Security restricts every row to its owner.</li>
          <li><strong>Cloudflare Pages</strong> — serves the app's HTML/CSS/JS. Cloudflare logs basic request metadata (IP, user-agent) per its standard hosting privacy practices.</li>
          <li><strong>OpenAI API (primary) and Google Gemini API (fallback)</strong> — when you ask the app to identify a meal from a photo or description, that image/text is sent to OpenAI's GPT-4.1 Mini model for analysis. If OpenAI is unavailable, we fall back to Google's Gemini model. Each provider's data handling policy governs their respective interaction. We send only the image and your optional caption with these requests — no account info, no other personal data. We have NOT opted into either provider's data-sharing-for-training programs; your meal photos are not used to train AI models.</li>
          <li><strong>Open Food Facts</strong> — when you scan a barcode or search for a packaged product, the query is sent to the public Open Food Facts API. They receive the barcode/search term and your IP.</li>
        </ul>
      </Section>

      <Section title="What we do with it">
        <ul>
          <li>Show your meals/weights/goals back to you across your devices.</li>
          <li>Generate AI estimates of calories and macros from your photos or descriptions.</li>
          <li>Compute weekly insights and food suggestions.</li>
          <li>Enforce per-user rate limits on the AI endpoints.</li>
        </ul>
        <p>That's it. No advertising, no data sales, no profile-building.</p>
      </Section>

      <Section title="Your rights">
        <ul>
          <li><strong>Export</strong>: Settings → "Download my data" gives you a JSON file of everything we have on you.</li>
          <li><strong>Delete</strong>: Settings → "Delete account" permanently removes every row and every photo. This cannot be undone.</li>
          <li><strong>Correction / questions</strong>: email <a href="mailto:hello@thefoundationbriefing.com" className="text-emerald-400 underline">hello@thefoundationbriefing.com</a>.</li>
          <li>Under GDPR (EU) and CCPA (California) you have additional rights to access, rectify, or restrict processing of your data — exercise these by email.</li>
        </ul>
      </Section>

      <Section title="Retention">
        <p>
          We keep your data as long as your account exists. When you delete your account, all data is removed
          within 24 hours (the auth.users CASCADE deletes the database rows immediately; photos are removed in
          the same flow). Supabase point-in-time backups may contain residual copies for up to 7 days before
          they roll off.
        </p>
      </Section>

      <Section title="Children">
        <p>
          Tallyplate is not directed at children under 13 and we do not knowingly collect data from them.
          If you believe a child has signed up, email us and we'll delete the account.
        </p>
      </Section>

      <Section title="Changes">
        <p>
          When we update this policy materially we'll change the "Last updated" date above. Substantive
          changes will trigger an in-app notice.
        </p>
      </Section>
    </>
  );
}

function TermsContent() {
  return (
    <>
      <h2 className="text-lg font-semibold text-slate-100">Terms of Service</h2>
      <p className="text-xs text-slate-400">Last updated: May 2026</p>

      <Section title="The short version">
        <p>
          Tallyplate is a personal nutrition tracker. The AI estimates are <strong>estimates</strong>, not
          medical advice. Use them as a rough guide, not a clinical input. Don't try to break the service or
          use it to harm others.
        </p>
      </Section>

      <Section title="Not medical advice">
        <p>
          The calorie/macro estimates the app produces are AI-generated and can be wildly wrong. Do not rely
          on them for medical decisions, eating-disorder recovery, athletic performance plans, or anything
          else where accuracy matters. Consult a registered dietitian or doctor for those use cases.
        </p>
      </Section>

      <Section title="Your account">
        <ul>
          <li>You're responsible for keeping your password secret.</li>
          <li>You must be 13 or older to use the service.</li>
          <li>One account per person.</li>
        </ul>
      </Section>

      <Section title="Acceptable use">
        <p>You agree not to:</p>
        <ul>
          <li>Send content that's illegal, abusive, or infringes someone else's rights.</li>
          <li>Try to break, overload, or reverse-engineer the service.</li>
          <li>Scrape, mirror, or resell the data or AI outputs.</li>
          <li>Use the service to spam, phish, or impersonate.</li>
        </ul>
      </Section>

      <Section title="AI features">
        <p>
          Photo and text estimates are powered by OpenAI (primary) and Google Gemini (fallback). We may rate-limit, suspend, or remove these
          features at any time if the cost/abuse profile changes.
        </p>
      </Section>

      <Section title="Termination">
        <p>
          You can delete your account at any time from Settings. We can suspend or close accounts that
          violate these terms.
        </p>
      </Section>

      <Section title="No warranties">
        <p>
          The service is provided "as is" with no warranties of any kind. We don't guarantee uptime, accuracy,
          or fitness for any particular purpose.
        </p>
      </Section>

      <Section title="Limitation of liability">
        <p>
          To the maximum extent permitted by law, our total liability for any claim related to the service
          is limited to the amount you've paid us in the last 12 months (which, for free accounts, is $0).
        </p>
      </Section>

      <Section title="Changes">
        <p>
          We may update these terms. Material changes will be announced in-app. Continued use after a change
          means you accept the new terms.
        </p>
      </Section>

      <Section title="Governing law">
        <p>These terms are governed by the laws of the United States. Disputes go to the courts there.</p>
      </Section>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-base font-semibold text-slate-100">{title}</h3>
      <div className="text-sm text-slate-300 space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_a]:underline">
        {children}
      </div>
    </section>
  );
}
